import React, { Fragment } from 'react';
import { Redirect, Route, withRouter } from 'react-router';
import { Switch } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { compose, mapProps, withProps } from 'recompose';
import { isDefinedAndNotNull } from '@/se/utilities/check';
import Page from '@/se/components/Page';
import Sidebar from '@/se/components/Sidebar';
import compact from 'lodash/compact';
import Footer from './Footer';
import Header from './Header';
import ChangePassword from '../ChangePassword';
import { dark as darkTheme } from '../../../../theme';
import ThemeBasedCSS from '../../../../components/ThemeBasedCSS';
import { unpackSessionObject } from '../../unpackSessionObject';
import { withSession } from '../../../../state/Session';
import { Context as PatientNameFormatContext } from '../../../../contexts/PatientNameFormat';
import get from 'lodash/fp/get';
import { muiThemeDark } from '../../../../muiTheme';
import { ThemeProvider as MUIThemeProvider } from '@material-ui/core/styles';
import { CssBaseline } from '@material-ui/core';
import { MainContainer } from '../../MainContainer';
import { OmniSearchProvider, OmniSearchSources } from '../../../OmniSearch';
import activeAndPastProceduresSource from '../../../../omnisearch/sources/hospital/activeAndPastProcedures';
import scheduledProceduresSource from '../../../../omnisearch/sources/hospital/scheduledProcedures';
import { Schedule } from '@material-ui/icons';
import { withScope } from '../../../../contexts/ScopeContext';
import ScheduledProcedures from '../../../entities/procedures/ScheduledProcedures';

const getRoutes = (basePath, baseUrl, modules) =>
  [
    modules.patientRegistration
      ? {
          path: `${basePath}/scheduled-patients`,
          to: `${baseUrl}/scheduled-patients`,
          label: 'PreOp Registration',
          icon: <Schedule />,
          component: ScheduledProcedures,
        }
      : undefined,
  ].filter(_ => !!_);

const getBasePathAndUrl = match => {
  if (!isDefinedAndNotNull(match) || !isDefinedAndNotNull(!match.path) || !isDefinedAndNotNull(!match.url)) {
    return ['', ''];
  }
  return [match.path === '/' ? '' : match.path, match.url === '/' ? '' : match.url];
};

const AdminSidebar = compose(
  withSession(unpackSessionObject),
  withRouter,
  withScope,
  withProps({
    Header,
    links: [],
    Footer,
  }),
  mapProps(props => {
    const { organization, match, search } = props;
    const organizationId = organization || match.params.organizationId;
    const [basePath, baseUrl] = getBasePathAndUrl(match);

    return {
      ...props,
      links: compact([
        ...getRoutes(
          basePath,
          baseUrl,
          Object.assign(
            {},
            {
              covidScreening: !!props.scope?.hospital?.covidScreening,
              ospitekOrdersIntegrations: !!props.scope?.hospital?.ospitekOrdersIntegrations?.externalLink?.enabled,
            },
            get('scope.hospital.modules')(props)
          ),
          organizationId,
          props.scope?.hospital?.ospitekOrdersIntegrations
        ),
      ]),
    };
  })
)(Sidebar);

const Homepage = props => {
  const [basePath, baseUrl] = getBasePathAndUrl(props.match);
  const modules = {
    patientRegistration: true,
    temperatureReporting: true,
    acceptsProcedureTransfer: true,
    covidScreening: Boolean(props.scope?.hospital?.covidScreening),
    inTakeForms: Boolean(props.scope?.hospital?.modules?.inTakeForms),
  };
  const switchRoutes = compact(getRoutes(basePath, baseUrl, modules)).filter(_ => !_.separator);
  return (
    <ThemeProvider theme={darkTheme}>
      <MUIThemeProvider theme={muiThemeDark}>
        <style>
          {`body {
            background: ${muiThemeDark.palette.background.default};
          }`}
        </style>
        <CssBaseline />
        <PatientNameFormatContext.Provider value={props.patientNameFormat || 'FullName'}>
          <OmniSearchProvider>
            <OmniSearchSources sources={[activeAndPastProceduresSource, scheduledProceduresSource]} />
            <Fragment>
              <Page Sidebar={AdminSidebar}>
                <MainContainer switchControl>
                  <Switch>
                    <Route exact path={`${basePath}/`} render={() => <Redirect to={`${baseUrl}/patients`} />} />
                    {switchRoutes.map(({ path, component, subPages }, i) => [
                      path ? <Route key={i} path={path} component={component} /> : null,
                      ...(subPages
                        ? subPages.map(subPage => (
                            <Route
                              key={path ? `${path}${subPage.path}` : subPage.path}
                              path={path ? `${path}${subPage.path}` : subPage.path}
                              component={subPage.component}
                            />
                          ))
                        : []),
                    ])}
                    <Route path="/change-password" component={ChangePassword} />
                  </Switch>
                </MainContainer>
              </Page>
              <ThemeBasedCSS />
            </Fragment>
          </OmniSearchProvider>
        </PatientNameFormatContext.Provider>
      </MUIThemeProvider>
    </ThemeProvider>
  );
};

export default compose(
  withSession(unpackSessionObject),
  withRouter,
  withSession(get('session.account.metadata')),
  withScope
)(Homepage);
